import { useEffect, useMemo, useState } from "react";
import { UserRound } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supportApi, type AssignableHandler, type AssigneeKind } from "@/lib/api/support";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";

const UNASSIGNED_VALUE = "__unassigned__";

function encode(kind: AssigneeKind, id: string): string {
  return `${kind}:${id}`;
}

function decode(value: string): { kind: AssigneeKind; id: string } | null {
  if (value === UNASSIGNED_VALUE) return null;
  const idx = value.indexOf(":");
  if (idx === -1) return null;
  const kind = value.slice(0, idx);
  const id = value.slice(idx + 1);
  if (kind !== "STAFF" && kind !== "USER") return null;
  return { kind, id };
}

function handlerLabel(h: AssignableHandler): string {
  const name = [h.firstName, h.lastName].filter(Boolean).join(" ");
  const display = name || h.email;
  return h.jobTitle?.name ? `${display} · ${h.jobTitle.name}` : display;
}

export type AssigneeValue = { kind: AssigneeKind; id: string } | null;

interface TicketAssigneePickerProps {
  value: AssigneeValue;
  onChange: (next: AssigneeValue) => Promise<void> | void;
  disabled?: boolean;
}

export function TicketAssigneePicker({ value, onChange, disabled }: TicketAssigneePickerProps) {
  const toast = useToast();
  const [options, setOptions] = useState<AssignableHandler[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supportApi
      .assignableHandlers()
      .then((data) => {
        if (!cancelled) setOptions(data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(getApiErrorMessage(err, "Failed to load staff"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const selectValue = value ? encode(value.kind, value.id) : UNASSIGNED_VALUE;

  const groupedOptions = useMemo(() => {
    const staff = options.filter((o) => o.kind === "STAFF");
    const users = options.filter((o) => o.kind === "USER");
    return { staff, users };
  }, [options]);

  const hasAnyOption = options.length > 0;

  return (
    <div className="flex items-center gap-2">
      <UserRound className="h-4 w-4 text-slate-400" />
      <Select
        disabled={disabled || loading}
        value={selectValue}
        onValueChange={async (v) => {
          await onChange(decode(v));
        }}
      >
        <SelectTrigger className="h-9 w-[260px]">
          <SelectValue placeholder={loading ? "Loading…" : "Unassigned"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={UNASSIGNED_VALUE}>Unassigned</SelectItem>
          {groupedOptions.staff.length > 0 && (
            <div className="px-2 pt-2 pb-1 text-[11px] uppercase tracking-wider text-slate-400">
              Staff
            </div>
          )}
          {groupedOptions.staff.map((h) => (
            <SelectItem key={encode(h.kind, h.id)} value={encode(h.kind, h.id)}>
              {handlerLabel(h)}
            </SelectItem>
          ))}
          {groupedOptions.users.length > 0 && (
            <div className="px-2 pt-2 pb-1 text-[11px] uppercase tracking-wider text-slate-400">
              Admins & Librarians
            </div>
          )}
          {groupedOptions.users.map((h) => (
            <SelectItem key={encode(h.kind, h.id)} value={encode(h.kind, h.id)}>
              {handlerLabel(h)}
            </SelectItem>
          ))}
          {!loading && !hasAnyOption && (
            <div className="px-2 py-3 text-xs text-slate-500">
              No staff available to assign yet.
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
